import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import useApiGet from "../hooks/getData";
import toast from "react-hot-toast";
import axios from "axios";
import { useSelector } from "react-redux";
import Searchbar from "../../components/Search";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";

// gifts_catalog.category values as the backend uses them
// (ChatController CP_GIFT_CATEGORY = 2, SocketController: 1 = All Gifts, 2 = CP Special)
const GIFT_CATEGORIES = [
  { id: 1, name: "All Gifts" },
  { id: 2, name: "CP Special" },
];

const getCategoryName = (id) =>
  GIFT_CATEGORIES.find((c) => c.id === Number(id))?.name || `Category ${id}`;

const emptyForm = {
  id: null,
  gift_id: "",
  name: "",
  emoji: "",
  coin_cost: "",
  category: "",
  is_animated: false,
  sort_order: "",
};

function GiftList() {
  const token = Cookies.get("Snapta_Admin_Token");
  const { getData } = useApiGet();
  const isSidebarOpen = useSelector((state) => state.sidebar.isOpen);

  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchGifts = async () => {
    setLoading(true);
    try {
      const res = await getData("/gifts/catalog");
      // API shape: { success, data: { categories: { data: [...] }, total }, message }
      const list = res?.data?.categories?.data || [];
      setGifts(list);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load gift list.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGifts();
    // eslint-disable-next-line
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setIsEdit(false);
  };

  const openAddForm = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditForm = (gift) => {
    setForm({
      id: gift.id,
      gift_id: gift.gift_id || "",
      name: gift.name || "",
      emoji: gift.emoji || "",
      coin_cost: gift.coin_cost || "",
      category: gift.category || "",
      is_animated: !!gift.is_animated,
      sort_order: gift.sort_order || "",
    });
    setIsEdit(true);
    setModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.gift_id || !form.name || !form.emoji || !form.coin_cost || !form.category) {
      toast.error("Please fill all required fields.");
      return;
    }

    const payload = {
      gift_id: form.gift_id,
      name: form.name,
      emoji: form.emoji,
      coin_cost: Number(form.coin_cost),
      category: Number(form.category),
      is_animated: form.is_animated ? 1 : 0,
      sort_order: Number(form.sort_order) || 0,
    };

    setSaving(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const endpoint = isEdit ? `gifts/edit/${form.id}` : "gifts/add";

      const res = await axios.post(`${apiUrl}/${endpoint}`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res?.data?.success) {
        toast.success(res.data.message || "Saved successfully!");
        setModalOpen(false);
        resetForm();
        fetchGifts();
      } else {
        toast.error(res?.data?.message || "Something went wrong.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await axios.post(
        `${apiUrl}/gifts/delete/${deleteId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res?.data?.success) {
        toast.success(res.data.message || "Gift deleted!");
        setDeleteId(null);
        fetchGifts();
      } else {
        toast.error(res?.data?.message || "Failed to delete.");
      }
    } catch (err) {
      toast.error("Failed to delete.");
    }
  };

  return (
    <div className={`dark:bg-primary ${isSidebarOpen ? "xl:pl-20" : "xl:pl-72"}`}>
      <Searchbar />
      <div className="px-4 pb-10 xl:px-6">

        <div className="flex items-center justify-between py-3">
          <h2 className="text-[#000000] font-semibold font-poppins text-xl pt-3 dark:text-darkText">
            Gift Catalog
          </h2>
          <button
            style={{ backgroundColor: "#0D9947" }}
            className="px-6 py-2.5 text-sm font-medium text-white rounded-lg font-poppins"
            onClick={openAddForm}
          >
            + Add Gift
          </button>
        </div>

        {loading ? (
          <p className="font-poppins dark:text-darkText">Loading...</p>
        ) : gifts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32">
            <h2 className="text-sm font-semibold font-poppins text-[#000000] dark:text-darkText">
              Don't have any data to show
            </h2>
          </div>
        ) : (
          <div className="overflow-x-auto border border-opacity-gradient dark:border-[#1F1F1F] rounded-lg">
            <table className="min-w-full text-sm text-left font-poppins">
              <thead style={{ backgroundColor: "#593A99" }} className="text-white">
                <tr>
                  <th className="p-3">ID</th>
                  <th className="p-3">Gift ID</th>
                  <th className="p-3">Emoji</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Coin Cost</th>
                  <th className="p-3">Category</th>
                  <th className="p-3">Sort Order</th>
                  <th className="p-3">Animated</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {gifts.map((g) => (
                  <tr key={g.id} className="border-t dark:border-[#1F1F1F] dark:text-darkText">
                    <td className="p-3">{g.id}</td>
                    <td className="p-3">{g.gift_id}</td>
                    <td className="p-3 text-xl">{g.emoji}</td>
                    <td className="p-3">{g.name}</td>
                    <td className="p-3">{g.coin_cost}</td>
                    <td className="p-3">{getCategoryName(g.category)}</td>
                    <td className="p-3">{g.sort_order}</td>
                    <td className="p-3">{g.is_animated ? "Yes" : "No"}</td>
                    <td className="p-3 flex gap-2">
                      <button
                        onClick={() => openEditForm(g)}
                        className="px-3 py-1.5 text-xs font-poppins text-[#593A99] border border-[#593A99] rounded-lg"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setDeleteId(g.id)}
                        className="px-3 py-1.5 text-xs font-poppins text-[#D92D20] border border-[#D92D20] rounded-lg"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{isEdit ? "Edit Gift" : "Add Gift"}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3 py-2">
            <div>
              <label className="text-sm font-poppins dark:text-darkText">Gift ID (unique, e.g. g13)</label>
              <input
                type="text"
                name="gift_id"
                value={form.gift_id}
                onChange={handleChange}
                className="w-full border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
              />
            </div>
            <div>
              <label className="text-sm font-poppins dark:text-darkText">Name</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
              />
            </div>
            <div>
              <label className="text-sm font-poppins dark:text-darkText">Emoji</label>
              <input
                type="text"
                name="emoji"
                value={form.emoji}
                onChange={handleChange}
                placeholder="🎁"
                className="w-full border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
              />
            </div>
            <div>
              <label className="text-sm font-poppins dark:text-darkText">Coin Cost</label>
              <input
                type="number"
                name="coin_cost"
                value={form.coin_cost}
                onChange={handleChange}
                className="w-full border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
              />
            </div>
            <div>
              <label className="text-sm font-poppins dark:text-darkText">Category</label>
              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                className="w-full border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
              >
                <option value="">Select category</option>
                {GIFT_CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-poppins dark:text-darkText">Sort Order</label>
              <input
                type="number"
                name="sort_order"
                value={form.sort_order}
                onChange={handleChange}
                className="w-full border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="is_animated"
                checked={form.is_animated}
                onChange={handleChange}
                id="gift_is_animated"
              />
              <label htmlFor="gift_is_animated" className="text-sm font-poppins dark:text-darkText">
                Is Animated
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-3">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-6 py-2.5 text-sm font-medium border border-header rounded-lg font-poppins dark:text-darkText"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{ backgroundColor: "#593A99" }}
                className="px-6 py-2.5 text-sm font-medium text-white rounded-lg font-poppins"
              >
                {saving ? "Saving..." : isEdit ? "Update" : "Add"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Modal */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Gift?</DialogTitle>
        <DialogContent>
          <p className="pb-4 font-poppins dark:text-darkText">This action cannot be undone. Are you sure?</p>
          <div className="flex justify-end gap-2 pb-2">
            <button
              onClick={() => setDeleteId(null)}
              className="px-6 py-2.5 text-sm font-medium border border-header rounded-lg font-poppins dark:text-darkText"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              style={{ backgroundColor: "#D92D20" }}
              className="px-6 py-2.5 text-sm font-medium text-white rounded-lg font-poppins"
            >
              Delete
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default GiftList;